export const tryExportPenSetPreview = async ({
  controller,
  currentView,
  configData,
  getGlobalSelectedOptions,
  setGlobalSelectedOptions,
}) => {
  try {
    if (!controller || !currentView) return null;

    const qtyMap = (getGlobalSelectedOptions && getGlobalSelectedOptions()?.ColorQtyMap?.value) || (typeof window !== 'undefined' ? window.ColorQtyMap : null);
    const opt1 = configData?.SupportedModels?.Options?.Option1;
    const opt1Method = String(opt1?.displayMethod || '').trim().toLowerCase();
    const isQtyColorGrid = opt1Method === 'colorswatchgridqty';

    const qtyEntries = qtyMap && typeof qtyMap === 'object'
      ? Object.entries(qtyMap).filter(([, q]) => Number(q) > 0)
      : [];

    const totalQty = qtyEntries.reduce((s, [, q]) => s + (Number(q) || 0), 0);

    if (!isQtyColorGrid || qtyEntries.length === 0) return null;

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const loadImageFromDataUrl = (dataUrl) => new Promise((resolve, reject) => {
      try {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = (e) => reject(e);
        img.src = dataUrl;
      } catch (e) {
        reject(e);
      }
    });

    const mergePreviews = async (previewByColor, slotColors) => {
      const angleDeg = -90;
      const angle = (angleDeg * Math.PI) / 180;

      const firstUrl = slotColors.find(c => !!previewByColor[c])
        ? previewByColor[slotColors.find(c => !!previewByColor[c])]
        : null;
      if (!firstUrl) return null;

      const colorImgMap = {};
      const colorsNeeded = Array.from(new Set(slotColors.filter(Boolean)));
      for (const c of colorsNeeded) {
        const u = previewByColor[c];
        if (!u) continue;
        try {
          colorImgMap[c] = await loadImageFromDataUrl(u);
        } catch (_) {
          // ignore
        }
      }

      const fallbackImg = colorImgMap[colorsNeeded.find(c => !!colorImgMap[c])] || (await loadImageFromDataUrl(firstUrl));

      const dims = Object.values(colorImgMap);
      const maxW = Math.max(
        fallbackImg.naturalWidth || fallbackImg.width || 600,
        ...dims.map(img => img.naturalWidth || img.width || 0)
      );
      const maxH = Math.max(
        fallbackImg.naturalHeight || fallbackImg.height || 600,
        ...dims.map(img => img.naturalHeight || img.height || 0)
      );

      const drawW = maxW || (fallbackImg.naturalWidth || fallbackImg.width || 600);
      const drawH = maxH || (fallbackImg.naturalHeight || fallbackImg.height || 600);

      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const rotW = Math.abs(drawW * cos) + Math.abs(drawH * sin);
      const rotH = Math.abs(drawW * sin) + Math.abs(drawH * cos);

      const count = Math.min(slotColors.length, 6);
      const overlap = 0.2;
      const stepX = rotW * (1 - overlap);
      const stepY = 0;

      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(rotW + Math.max(0, count - 1) * stepX);
      canvas.height = Math.ceil(rotH + Math.max(0, count - 1) * stepY);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      try {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      } catch (_) {}

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < count; i++) {
        const color = slotColors[i];
        const url = previewByColor[color];
        if (!url) continue;

        const img = colorImgMap[color] || fallbackImg;
        const baseX = i * stepX;
        const baseY = 0;

        ctx.save();
        ctx.translate(baseX + rotW / 2, baseY + rotH / 2);
        ctx.rotate(angle);
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();
      }

      return canvas.toDataURL('image/png');
    };

    const originalGlobalOptions = (getGlobalSelectedOptions && getGlobalSelectedOptions()) || {};
    const originalOpt1 = originalGlobalOptions?.Option1;

    if (controller.productId && currentView) {
      try {
        await controller.saveDesign(controller.selectedView || currentView);
      } catch (e) {
        console.warn('saveDesign failed before pen export, continuing...', e);
      }
    }

    try {
      const getViewId = (view) => {
        if (!view) return '';
        if (typeof view === 'string') return view;
        if (typeof view === 'object' && view.id) return view.id;
        return String(view);
      };
      const viewId = getViewId(currentView);
      if (getViewId(controller.selectedView) !== viewId) {
        controller.clearDesignLayer();
        await controller.setSelectedView(currentView);
        await sleep(300);
      }
      if (controller.waitForViewReady) {
        await controller.waitForViewReady(viewId, 4000);
      }
      if (controller.loadDesign) {
        await controller.loadDesign(currentView);
        await sleep(80);
      }
    } catch (e) {
      console.warn('pen export: failed to ensure view ready', e);
    }

    const previewByColor = {};
    let sharedDesign = null;
    for (let i = 0; i < qtyEntries.length; i++) {
      const colorVal = qtyEntries[i][0];
      try {
        const nextOptions = { ...(originalGlobalOptions || {}) };
        nextOptions.Option1 = { value: colorVal };
        if (originalGlobalOptions?.ColorQtyMap) nextOptions.ColorQtyMap = originalGlobalOptions.ColorQtyMap;
        setGlobalSelectedOptions(nextOptions);

        if (controller.reload) {
          await controller.reload();
        }
        await sleep(120);

        try {
          controller.clearDesignLayer?.();
          if (controller.loadDesign) {
            await controller.loadDesign(currentView);
            await sleep(60);
          }
        } catch (_) {}

        const result = await controller.exportProduct({});
        if (result?.preview) {
          previewByColor[colorVal] = result.preview;
        }
        if (!sharedDesign && result?.design) {
          sharedDesign = result.design;
        }
      } catch (e) {
        console.warn('pen export failed for color, continuing...', { colorVal, error: e });
      }
    }

    const slotColors = [];
    qtyEntries.forEach(([c, q]) => {
      const n = Number(q) || 0;
      for (let i = 0; i < n; i++) slotColors.push(c);
    });
    while (slotColors.length < 6) slotColors.push(slotColors[slotColors.length - 1] || qtyEntries[0]?.[0] || '');
    if (slotColors.length > 6) slotColors.length = 6;

    const mergedPreview = await mergePreviews(previewByColor, slotColors);

    try {
      const restored = { ...(originalGlobalOptions || {}) };
      if (originalOpt1 !== undefined) restored.Option1 = originalOpt1;
      setGlobalSelectedOptions(restored);
      if (controller.reload) {
        await controller.reload();
      }
      try {
        controller.clearDesignLayer?.();
        if (controller.loadDesign) await controller.loadDesign(currentView);
      } catch (_) {}
    } catch (e) {
      console.warn('pen export: failed to restore options', e);
    }

    return {
      allImages: { previews: [], designs: [] },
      tempAllViewsImages: [
        {
          view: currentView,
          designIndex: 1,
          preview: mergedPreview || null,
          design: sharedDesign || null,
          meta: {
            pen: {
              counts: qtyMap,
              slots: slotColors,
              total: totalQty,
            },
          },
        },
      ],
    };
  } catch (e) {
    console.warn('pen export helper failed', e);
    return null;
  }
};
